import { useTranslation } from "react-i18next";
import { Gauge, Pencil, TriangleAlert } from "lucide-react";
import type { HandspeedSensor } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMutating } from "@tanstack/react-query";
import { HANDSPEED_MUTATION_KEY, useManualHandspeed, useSetManualHandspeed, type ManualMutationError } from "./useManualMutations.js";

export interface ManualHandspeedSectionProps {
  manualId: number;
  session: AuthSession | null;
  /** The drawer's View/Edit gate. The read itself is edit-authz'd server-side (D2), so a view-only caller must not
   *  even fetch — the block simply isn't theirs, exactly as the legacy's settings window isn't. */
  canEdit: boolean;
  /** Open the device picker, layered OVER the drawer (the Manage-reviewers pattern). */
  onOpenPicker: () => void;
  /** Report the flag upward so the drawer's "Handspeed" type badge and the grid card stay truthful without a reopen. */
  onEnabledChange: (enabled: boolean) => void;
}

/**
 * The manual's hand-speed rig (ADR-039) — the legacy's `handspeedCheck` switch and its "Ausgewählte Sensoren" box,
 * which live in a manual-settings window the rewrite has no counterpart for (D1).
 *
 * The switch and the list are two different facts: the flag says whether the rig is IN USE, the list says which
 * devices it uses. The legacy conflates them — it posts `sensors: []` on every toggle, so switching off and on again
 * destroys a configured rig silently. We keep the links (D3), which is why turning the switch back on restores the
 * devices instead of an empty box.
 */
export function ManualHandspeedSection({ manualId, session, canEdit, onOpenPicker, onEnabledChange }: ManualHandspeedSectionProps) {
  const { t } = useTranslation();
  const rig = useManualHandspeed(session, manualId, canEdit);
  const save = useSetManualHandspeed(session);
  // ANY hand-speed write, including the picker's — the two controls own separate mutation instances but write the
  // same list, and this switch sends whatever `data.sensors` currently holds. Acting on that while the picker's PATCH
  // is still in flight replaces the list with pre-save ids and silently drops the devices it just saved (diff panel).
  const writing = useIsMutating({ mutationKey: HANDSPEED_MUTATION_KEY }) > 0;

  if (!canEdit) return null;

  const data = rig.data;
  const enabled = data?.enabled ?? false;
  const sensors = data?.sensors ?? [];
  const error = save.error as ManualMutationError | undefined;

  const toggle = (next: boolean) => {
    if (!data || writing) return;
    // The list travels with the flag, unchanged — the whole point of D3.
    save.mutate(
      { id: manualId, enabled: next, sensorIds: data.sensors.map((s) => s.id) },
      { onSuccess: (res) => onEnabledChange(res.enabled) },
    );
  };

  return (
    <section aria-label={t("manualManagement.handspeedSection.title")} className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t("manualManagement.handspeedSection.title")}</h3>
        {data && enabled ? (
          <Button variant="outline" size="sm" onPress={onOpenPicker} isDisabled={writing}>
            <Pencil aria-hidden="true" className="size-3.5" />
            {t("manualManagement.handspeedSection.change")}
          </Button>
        ) : null}
      </div>

      {rig.isPending ? (
        <p className="text-muted-foreground text-sm">{t("common.loading")}</p>
      ) : rig.isError ? (
        <p className="text-muted-foreground text-sm">{t("manualManagement.handspeedSection.loadFailed")}</p>
      ) : (
        <>
          <Switch isSelected={enabled} isDisabled={writing} onChange={toggle}>
            {t("manualManagement.handspeedSection.check")}
          </Switch>

          {/* The device box appears only with the rig switched on — the legacy reveals it the same way. The links
              survive the switch, so turning it back on brings the same devices back. */}
          {enabled ? (
            sensors.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("manualManagement.handspeedSection.none")}{" "}
                <button type="button" className="text-primary underline underline-offset-2" onClick={onOpenPicker}>
                  {t("manualManagement.handspeedSection.choose")}
                </button>
              </p>
            ) : (
              <ul className="divide-border flex flex-col divide-y">
                {sensors.map((s) => (
                  <HandspeedRow key={s.id} sensor={s} />
                ))}
              </ul>
            )
          ) : null}

          {error ? (
            <p className="text-destructive text-sm">
              {error.code === "HANDSPEED_UNAVAILABLE"
                ? t("manualManagement.handspeedSection.deviceGone")
                : error.code === "MANUAL_BODY_STALE"
                  ? t("manualManagement.handspeedSection.stale")
                  : t("manualManagement.handspeedSection.saveFailed")}
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/**
 * One bound device: the slug (what is printed on the hardware) then its display name, the legacy's
 * `slug + " " + displayName`.
 *
 * A device that is NOT a `HandSpeedSensor` can be bound — production holds exactly one, and the legacy shows it in
 * this box while its picker can never see it, so it can never be taken off. We say why it is odd and the picker lets
 * it be removed (D4).
 */
function HandspeedRow({ sensor }: { sensor: HandspeedSensor }) {
  const { t } = useTranslation();
  return (
    <li className="flex items-center gap-2 py-2 text-sm first:pt-0 last:pb-0">
      <Gauge aria-hidden="true" className="text-muted-foreground size-4 shrink-0" />
      <span className="bg-muted text-muted-foreground shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-medium tabular-nums">
        {sensor.slug}
      </span>
      <span className="truncate">{sensor.displayName ?? sensor.slug}</span>
      {!sensor.isHandSpeed ? (
        <TooltipTrigger delay={0}>
          <Button variant="ghost" size="icon-sm" aria-label={t("manualManagement.handspeedSection.notHandspeedAria", { slug: sensor.slug })}>
            <TriangleAlert className="text-attention size-3.5" />
          </Button>
          <Tooltip>{t("manualManagement.handspeedSection.notHandspeed")}</Tooltip>
        </TooltipTrigger>
      ) : null}
    </li>
  );
}
